'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import SiteNav from '@/components/SiteNav';
import LandingFooter from '@/app/landing-page/components/LandingFooter';
import { ArrowRight, Copy, Check } from 'lucide-react';

function CopyPromptBox({ children }: { children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const plainText = typeof children === 'string' ? children : '';
    navigator.clipboard.writeText(plainText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className="relative rounded-xl p-6 my-6"
      style={{ backgroundColor: 'rgba(212,137,26,0.05)', border: '1px solid rgba(212,137,26,0.25)', borderLeft: '4px solid #D4891A' }}
    >
      <div className="font-body text-sm pr-10" style={{ color: '#2d3748', lineHeight: '1.85' }}>
        {children}
      </div>
      <button
        onClick={handleCopy}
        className="absolute top-4 right-4 flex items-center gap-1.5 font-body text-xs font-semibold px-3 py-1.5 rounded-lg transition-all duration-150"
        style={{ backgroundColor: copied ? 'rgba(212,137,26,0.15)' : 'rgba(212,137,26,0.1)', color: '#D4891A' }}
        aria-label="Copy prompt"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

export default function AiForMeetingPreparationPage() {
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "How AI Can Help You Prepare For Meetings",
    "url": "https://thatclicked.com/learn/ai-for-meeting-preparation",
    "author": {
      "@type": "Person",
      "name": "Anna Rippon",
      "url": "https://thatclicked.com/about"
    },
    "publisher": {
      "@type": "Organization",
      "name": "That Clicked",
      "url": "https://thatclicked.com"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://thatclicked.com/learn/ai-for-meeting-preparation"
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F7F4' }}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <SiteNav currentPath="/learn/ai-for-meeting-preparation" />

      <main className="pt-24 pb-24 lg:pt-36 lg:pb-36">
        <div className="max-w-2xl mx-auto px-6 lg:px-0">

          <div className="flex items-center gap-2 mb-6 mt-8">
            <div className="h-px w-8 flex-shrink-0" style={{ backgroundColor: '#D4891A' }} aria-hidden="true" />
            <span className="font-body text-xs font-semibold uppercase tracking-widest" style={{ color: '#D4891A', letterSpacing: '0.12em' }}>
              By Anna Rippon, ThatClicked
            </span>
          </div>

          <h1
            className="font-display font-semibold mb-8"
            style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', color: '#1B2A4A', lineHeight: '1.18', letterSpacing: '-0.01em' }}
          >
            How AI Can Help You Prepare For Meetings
          </h1>

          <div className="h-px w-full mb-10" style={{ backgroundColor: 'rgba(27,42,74,0.12)' }} aria-hidden="true" />

          <div className="space-y-6 font-body" style={{ color: '#2d3748', lineHeight: '1.85', fontSize: '1.05rem' }}>

            <h2 className="font-display font-semibold pt-2" style={{ fontSize: '0.85rem', color: '#D4891A', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              THE SITUATION
            </h2>

            <p>
              The meeting is in forty minutes. You know the topic broadly. You have not had time to read the background document. You are not entirely sure who will be in the room or what they will want from you.
            </p>

            <p>
              Or: the meeting is a difficult one. A conversation you have been slightly dreading. A performance discussion, a project post-mortem, a negotiation where you want to go in prepared but have not had the time to prepare properly.
            </p>

            <p>
              Either way, forty minutes is not much. But it is enough — if you know how to use it.
            </p>

            <h2 className="font-display font-semibold pt-4" style={{ fontSize: '1.25rem', color: '#1B2A4A' }}>
              What AI can do before a meeting
            </h2>

            <h3 className="font-display font-semibold pt-2" style={{ fontSize: '1.1rem', color: '#1B2A4A' }}>
              Brief you on background material
            </h3>

            <p>
              If there is a document, a report, or a brief you have not had time to read, AI can summarise it in the time it takes to make a coffee.
            </p>

            <p>
              Paste the content in and ask it: &ldquo;What are the three most important things I need to understand before a meeting about this?&rdquo; What comes back gives you enough to participate meaningfully without having read every word.
            </p>

            <h3 className="font-display font-semibold pt-2" style={{ fontSize: '1.1rem', color: '#1B2A4A' }}>
              Prepare you for difficult conversations
            </h3>

            <p>
              If the meeting involves a challenging discussion — a stakeholder who tends to push back hard, a topic where you expect resistance, a decision you need to defend — AI is useful for anticipating what will come at you.
            </p>

            <p>
              Tell it who will be in the room, what you are proposing, and what the likely objections are. Ask it to give you the five strongest counterarguments someone might make. Then prepare your responses.
            </p>

            <p>
              This is not a gimmick. It is the same preparation good lawyers do before court. You are thinking through the room before you are in it.
            </p>

            <h3 className="font-display font-semibold pt-2" style={{ fontSize: '1.1rem', color: '#1B2A4A' }}>
              Generate questions worth asking
            </h3>

            <p>
              If you are attending rather than leading, AI can help you prepare questions that demonstrate you have engaged with the material — even if you have only had forty minutes with it.
            </p>

            <p>
              &ldquo;What are the most important questions someone should ask in a meeting about this?&rdquo; produces a useful starting list you can filter for the ones that are genuinely relevant to your situation.
            </p>

            <h3 className="font-display font-semibold pt-2" style={{ fontSize: '1.1rem', color: '#1B2A4A' }}>
              Produce an agenda or structure
            </h3>

            <p>
              If you are running the meeting, AI can produce a draft agenda in under two minutes. Give it the objective of the meeting, the time available, and the names or roles of who will be there. It will organise the time sensibly.
            </p>

            <p>
              This is not a replacement for your judgment about what the meeting needs. It is a starting point that saves you staring at a blank document.
            </p>

            <h2 className="font-display font-semibold pt-4" style={{ fontSize: '1.25rem', color: '#1B2A4A' }}>
              What AI cannot do for a meeting
            </h2>

            <p>
              AI does not know the room. It does not know the relationships, the history, or the unspoken dynamics. It can prepare you for a generic version of the meeting. You have to apply the preparation to the specific people and context involved.
            </p>

            <p>
              The output is a starting point, not a script. The best use of AI meeting preparation is to think through the likely shape of the conversation — then adapt in the room as you always have.
            </p>

            <h2 className="font-display font-semibold pt-4" style={{ fontSize: '1.25rem', color: '#1B2A4A' }}>
              A prompt that works
            </h2>

            <CopyPromptBox>
              I have a meeting in [X minutes/hours] about [describe the topic]. The people attending are [describe roles or names]. I want to [achieve this outcome]. What are the three most important things I should know or prepare before I go in? Also give me two or three questions worth asking.
            </CopyPromptBox>

            <p>
              This single prompt will give you a usable brief for almost any meeting. Read what comes back, filter for what is actually relevant, and walk in better prepared than you would have been.
            </p>

            <h2 className="font-display font-semibold pt-4" style={{ fontSize: '1.25rem', color: '#1B2A4A' }}>
              Honest expectations
            </h2>

            <p>
              Meeting preparation with AI is one of the most immediately useful applications for professionals. The investment is small — five to ten minutes of prompting — and the return is significant.
            </p>

            <p>
              The biggest gains are in two areas: briefing yourself on material you have not had time to read, and preparing for difficult or high-stakes conversations. Both are genuinely better with AI than without it.
            </p>

          </div>

          {/* Where to go deeper */}
          <div className="mt-14 rounded-xl p-8 border" style={{ backgroundColor: 'white', borderColor: 'rgba(27,42,74,0.12)' }}>
            <h2 className="font-display font-semibold mb-5" style={{ fontSize: '1.15rem', color: '#1B2A4A' }}>
              Where to go deeper
            </h2>
            <p className="font-body text-sm mb-5" style={{ color: '#2d3748', lineHeight: '1.75' }}>
              Module 1 covers Copilot in Teams — including how to use it during and after meetings for summaries and action tracking. Module 3 covers communication at work more broadly, including how AI can help you prepare for difficult conversations.
            </p>
            <ul className="space-y-2">
              <li>
                <Link href="/free-lesson-screen" className="font-body text-sm" style={{ color: '#1B2A4A' }}>
                  → Module 1: Copilot at Work <span style={{ color: '#6b7280' }}>(paid)</span>
                </Link>
              </li>
              <li>
                <Link href="/free-lesson-screen" className="font-body text-sm" style={{ color: '#1B2A4A' }}>
                  → Module 3: Communication at Work <span style={{ color: '#6b7280' }}>(paid)</span>
                </Link>
              </li>
              <li>
                <Link href="/learn/ai-for-emails" className="font-body text-sm" style={{ color: '#1B2A4A' }}>
                  → Related: How AI Can Help With Emails
                </Link>
              </li>
            </ul>
          </div>

          <div className="h-px w-full mt-14 mb-12" style={{ backgroundColor: 'rgba(27,42,74,0.12)' }} aria-hidden="true" />

          <div className="text-center">
            <p className="font-body text-base mb-6" style={{ color: 'rgba(27,42,74,0.7)' }}>That Clicked is a plain-English AI training platform for professionals over 50.</p>
            <Link
              href="/free-lesson-screen"
              className="group inline-flex items-center justify-center gap-2.5 font-body font-semibold text-base px-8 py-4 rounded-xl transition-all duration-200 active:scale-95"
              style={{ backgroundColor: '#D4891A', color: 'white' }}
            >
              Learn more AI skills — first two lessons free
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>

        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
